import React, { useState, Fragment } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Typography,
  styled,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Row, Column } from 'further-ui/components/Layout';
import Avatar from 'further-ui/components/Avatar';

type Props = {
  inputPlaceholder: string;
  buttonText?: string;
  inputMaxWidth?: number;
  value: Array<string>;
  duplicateValueErrorMessage?: string;
  onChange: (values: Array<string>) => void;
  validate?: (value: string) => boolean;
  validationErrorMessage?: string;
  formErrorMessages?: Array<{ message?: string }>;
};

const InputRow = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'inputMaxWidth',
})<{ inputMaxWidth: number }>(({ inputMaxWidth }) => ({
  ...(inputMaxWidth && { maxWidth: inputMaxWidth }),
  alignItems: 'flex-start',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(theme.spacingSizes.xxs),
}));

const ContactItem = styled(Row)(({ theme }) => ({
  justifyContent: 'space-between',
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const StyledTextField = styled(TextField)({
  flex: 1,
});

const MultipleEntryInput: React.FC<Props> = ({
  inputPlaceholder,
  buttonText = 'Add',
  inputMaxWidth = 400,
  value = [],
  duplicateValueErrorMessage = 'This value already exists.',
  onChange,
  validate,
  validationErrorMessage,
  formErrorMessages,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | undefined>();

  const handleAdd = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check for duplicates
    if (value.includes(trimmedValue)) {
      setError(duplicateValueErrorMessage);
      return;
    }

    // Run validation if provided
    if (validate) {
      const isValid = validate(trimmedValue);
      if (!isValid) {
        setError(validationErrorMessage);
        return;
      }
    }

    // Add the value
    onChange([...value, trimmedValue]);
    setInputValue('');
    setError(undefined);
  };

  const handleDelete = (index: number) => {
    const newValues = value.filter((_, i) => i !== index);
    onChange(newValues);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Column spacing="sm" fullWidth>
      <InputRow fullWidth spacing="sm" inputMaxWidth={inputMaxWidth}>
        <StyledTextField
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(undefined);
          }}
          onKeyDown={handleKeyDown}
          placeholder={inputPlaceholder}
          variant="outlined"
          size="small"
          error={!!error}
          helperText={error}
          fullWidth
        />
        <StyledButton
          variant="contained"
          onClick={handleAdd}
          size="large"
          disabled={!inputValue.trim()}
        >
          {buttonText}
        </StyledButton>
      </InputRow>

      {value.length > 0 && (
        <Column spacing="xs" fullWidth>
          {value.map((value, index) => (
            <Fragment key={index}>
              <ContactItem key={`${value}-${index}`}>
                <Row spacing="xs" centered>
                  <Avatar text={value} color="random" size="md" />
                  <Typography variant="body2" color="grey.900">
                    {value}
                  </Typography>
                </Row>
                <IconButton size="small" onClick={() => handleDelete(index)}>
                  <Delete fontSize="small" />
                </IconButton>
              </ContactItem>
              {formErrorMessages?.[index] && (
                <Typography variant="body2" color="error">
                  {formErrorMessages[index]?.message}
                </Typography>
              )}
            </Fragment>
          ))}
        </Column>
      )}
    </Column>
  );
};

export default MultipleEntryInput;
